/**
 * Supabase Client Singleton
 *
 * Single instance of Supabase client using anon key only.
 * All data access goes through RLS policies.
 *
 * Error normalization ensures consistent error handling across the app.
 */

import { createClient } from '@supabase/supabase-js'
import { env } from './env'

// Create Supabase client singleton
export const supabase = createClient(
  env.VITE_SUPABASE_URL,
  env.VITE_SUPABASE_ANON_KEY,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  }
)

/**
 * Normalize errors to consistent format
 *
 * Converts various error types to { code, message } format.
 * Never exposes raw database errors to users.
 *
 * @param {Error|Object} error - The error to normalize
 * @returns {{ code: string, message: string }}
 */
export function normalizeError(error) {
  // Already normalized
  if (error?.code && error?.message) {
    return { code: error.code, message: error.message }
  }

  // Supabase/Postgres errors
  if (error?.code) {
    return {
      code: error.code,
      message: getUserFriendlyMessage(error),
    }
  }

  // Generic errors
  if (error?.message) {
    return {
      code: 'UNKNOWN_ERROR',
      message: error.message,
    }
  }

  // Fallback
  return {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred. Please try again.',
  }
}

/**
 * Convert technical error codes to user-friendly messages
 *
 * @param {Object} error - Supabase error object
 * @returns {string} User-friendly error message
 */
function getUserFriendlyMessage(error) {
  const errorMap = {
    // Auth errors
    'invalid_credentials': 'Invalid email or password.',
    'email_not_confirmed': 'Please confirm your email address.',
    'user_already_exists': 'An account with this email already exists.',
    'weak_password': 'Password must be at least 8 characters.',
    'signup_disabled': 'Sign up is currently disabled.',

    // RLS policy violations
    '42501': 'You do not have permission to perform this action.',
    'PGRST301': 'You do not have permission to perform this action.',

    // Constraint violations
    '23505': 'This record already exists.',
    '23503': 'Cannot delete: this record is referenced by other data.',
    '23514': 'Invalid data: constraint check failed.',

    // RPC errors
    'P0001': error.message || 'Operation failed.',
    'P0002': 'Record not found.',
    'P0003': 'Multiple records found when one was expected.',

    // Network errors
    'ECONNREFUSED': 'Unable to connect to the server.',
    'ETIMEDOUT': 'Request timed out. Please try again.',
  }

  return errorMap[error.code] || error.message || 'An error occurred. Please try again.'
}

/**
 * Check if user is authenticated
 *
 * @returns {Promise<boolean>}
 */
export async function isAuthenticated() {
  const { data: { session } } = await supabase.auth.getSession()
  return !!session
}

/**
 * Get current user
 *
 * @returns {Promise<{data: User|null, error: Error|null}>}
 */
export async function getCurrentUser() {
  try {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    return { data: user, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}

/**
 * Get current user's profile
 *
 * @returns {Promise<{data: Profile|null, error: Error|null}>}
 */
export async function getCurrentProfile() {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser()
    if (userError) throw userError
    if (!user) return { data: null, error: null }

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()

    if (profileError) throw profileError
    return { data: profile, error: null }
  } catch (error) {
    return { data: null, error: normalizeError(error) }
  }
}
